import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { FormEvent } from 'react';

import {
  clientsQuery,
  createClient,
  setClientPriceGroup,
  type Client,
  type NewClient,
} from '../api/clients.ts';
import { accountManagersQuery, setClientAccountManager } from '../api/account-managers.ts';
import { priceGroupsQuery, type PriceGroup } from '../api/pricing.ts';
import { meQuery } from '../api/me.ts';
import { Button } from '../components/button.tsx';
import { AddButton } from '../components/action-buttons.tsx';
import { Field } from '../components/field.tsx';
import { PageHeader } from '../components/page-header.tsx';
import { AccountManagerPicker } from './account-manager-picker.tsx';

// CLI-01 and CLI-04's Phase 1 half. The screen shows what a client record actually is today —
// who they are, how to reach them, and when they get invoiced — and nothing it cannot do.

export function ClientsScreen() {
  const clients = useQuery(clientsQuery);
  const me = useQuery(meQuery);
  const [adding, setAdding] = useState(false);

  // The API refuses a write without clients.manage regardless (FND-01); hiding the control is so
  // that someone who cannot use it is not invited to try, never the enforcement.
  const mayManage = me.data?.capabilities.includes('clients.manage') ?? false;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        kicker="Clients"
        title="Clients"
        meta={
          clients.data
            ? `${clients.data.length} ${clients.data.length === 1 ? 'client' : 'clients'}`
            : undefined
        }
        actions={
          mayManage && !adding ? <AddButton noun="Client" onClick={() => setAdding(true)} /> : null
        }
      />

      {adding ? (
        <NewClientForm onDone={() => setAdding(false)} onCancel={() => setAdding(false)} />
      ) : null}

      {clients.isPending ? <p className="text-muted">Loading…</p> : null}

      {clients.isError ? (
        <p role="alert" className="alert">
          {clients.error.message}
        </p>
      ) : null}

      {clients.data?.length === 0 ? (
        <p className="text-ink-soft">
          No clients yet.{' '}
          {mayManage
            ? 'Add the first one above.'
            : 'Someone with permission can add the first one.'}
        </p>
      ) : null}

      {clients.data && clients.data.length > 0 ? (
        <ClientTable
          clients={clients.data}
          seesPrices={me.data?.canSeePrices ?? false}
          mayPrice={me.data?.capabilities.includes('prices.manage') ?? false}
          mayManage={mayManage}
        />
      ) : null}
    </div>
  );
}

function ClientTable({
  clients: rows,
  seesPrices,
  mayPrice,
  mayManage,
}: {
  clients: Client[];
  seesPrices: boolean;
  mayPrice: boolean;
  mayManage: boolean;
}) {
  // PRC-02: the group a client pays is pricing, so it is shown only above the price line.
  const groups = useQuery({ ...priceGroupsQuery, enabled: seesPrices });
  return (
    // Wide content scrolls inside its own container rather than the page.
    <div className="overflow-x-auto">
      <table className="data-table">
        <caption className="sr-only">Clients, with their main contact and payment terms</caption>
        <thead>
          <tr>
            <th scope="col">Name</th>
            <th scope="col">Kind</th>
            <th scope="col">Main contact</th>
            <th scope="col">Invoicing</th>
            <th scope="col">Account manager</th>
            {seesPrices ? <th scope="col">Price group</th> : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((client) => {
            const contact = client.contacts[0];
            return (
              <tr key={client.id}>
                <th scope="row" className="font-semibold text-ink">
                  {client.name}
                </th>
                <td className="text-ink-soft">
                  {client.kind === 'organisation' ? 'Organisation' : 'Individual'}
                </td>
                <td className="text-ink-soft">
                  {contact ? (
                    <>
                      {contact.name}
                      {contact.email || contact.phone ? (
                        <span className="block text-sm text-muted">
                          {contact.email ?? contact.phone}
                        </span>
                      ) : null}
                    </>
                  ) : (
                    <span className="text-muted">None recorded</span>
                  )}
                </td>
                <td className="text-ink-soft">
                  {client.paymentTerms === 'prepay'
                    ? 'Prepay — before book-out'
                    : `On account — ${client.accountDays} days`}
                </td>
                <td>
                  <AccountManagerCell client={client} mayManage={mayManage} />
                </td>
                {seesPrices ? (
                  <td>
                    <PriceGroupCell
                      client={client}
                      groups={groups.data ?? []}
                      mayPrice={mayPrice}
                    />
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function PriceGroupCell({
  client,
  groups,
  mayPrice,
}: {
  client: Client;
  groups: PriceGroup[];
  mayPrice: boolean;
}) {
  const queryClient = useQueryClient();
  const change = useMutation({
    mutationFn: (groupId: string | null) => setClientPriceGroup(client.id, groupId),
    onSettled: () => queryClient.invalidateQueries({ queryKey: clientsQuery.queryKey }),
  });
  const fallback = groups.find((g) => g.isDefault);
  const current = groups.find((g) => g.id === client.priceGroupId) ?? fallback;
  if (!mayPrice) return <span className="text-ink-soft">{current?.name ?? '—'}</span>;
  return (
    <label>
      <span className="sr-only">Price group for {client.name}</span>
      <select
        className="input py-1"
        value={client.priceGroupId ?? ''}
        disabled={change.isPending}
        onChange={(e) => change.mutate(e.currentTarget.value || null)}
      >
        <option value="">{fallback ? `${fallback.name} (default)` : 'Default'}</option>
        {groups
          .filter((g) => !g.isDefault)
          .map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}
            </option>
          ))}
      </select>
      {change.isError ? (
        <span role="alert" className="block text-sm text-accent-2-700">
          {change.error.message}
        </span>
      ) : null}
    </label>
  );
}

function AccountManagerCell({ client, mayManage }: { client: Client; mayManage: boolean }) {
  const queryClient = useQueryClient();
  const change = useMutation({
    mutationFn: (id: string | null) => setClientAccountManager(client.id, id),
    onSettled: () => queryClient.invalidateQueries({ queryKey: clientsQuery.queryKey }),
  });
  if (!mayManage) {
    return (
      <span className="text-ink-soft">
        {client.accountManager?.name ?? <span className="text-muted">Unassigned</span>}
      </span>
    );
  }
  return (
    <>
      <AccountManagerPicker
        label={`Account manager for ${client.name}`}
        hideLabel
        current={client.accountManager ?? null}
        disabled={change.isPending}
        onChange={(id) => change.mutate(id)}
      />
      {change.isError ? (
        <span role="alert" className="block text-sm text-accent-2-700">
          {change.error.message}
        </span>
      ) : null}
    </>
  );
}

function NewClientForm({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<'organisation' | 'individual'>('organisation');
  const [name, setName] = useState('');
  const [terms, setTerms] = useState<'prepay' | 'account'>('prepay');
  const [accountDays, setAccountDays] = useState('30');
  const [contactName, setContactName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  // CLI-01 at v1.984: undefined until changed, and then the API's default (whoever adds it, when
  // they can manage quotes) is what the picker shows and what is saved.
  const [manager, setManager] = useState<string | null | undefined>(undefined);
  const me = useQuery(meQuery);
  const people = useQuery(accountManagersQuery);
  const creatorEligible = people.data?.some((p) => p.id === me.data?.id) ?? false;

  const save = useMutation({
    mutationFn: (input: NewClient) => createClient(input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: clientsQuery.queryKey });
      onDone();
    },
  });

  // CLI-01: an individual is their own contact, so the form asks for one and the API requires it.
  const contactRequired = kind === 'individual';
  const hasContact = contactName.trim().length > 0 && (email.trim() || phone.trim());

  function submit(event: FormEvent) {
    event.preventDefault();
    save.mutate({
      kind,
      name: name.trim(),
      paymentTerms: terms,
      ...(terms === 'account' ? { accountDays: Number(accountDays) } : {}),
      ...(manager !== undefined ? { accountManagerId: manager } : {}),
      ...(hasContact
        ? {
            contact: {
              name: contactName.trim(),
              ...(email.trim() ? { email: email.trim() } : {}),
              ...(phone.trim() ? { phone: phone.trim() } : {}),
            },
          }
        : {}),
    });
  }

  return (
    <form onSubmit={submit} className="panel flex flex-col gap-4">
      <h2 className="section-title">Add a client</h2>

      {save.isError ? (
        <p role="alert" className="alert">
          {save.error.message}
        </p>
      ) : null}

      <fieldset className="flex flex-col gap-2">
        <legend className="text-sm font-medium text-ink">What kind of client is this?</legend>
        {(
          [
            ['organisation', 'A business or organisation'],
            ['individual', 'A private individual'],
          ] as const
        ).map(([value, label]) => (
          <label key={value} className="flex items-center gap-2 text-ink">
            <input
              type="radio"
              name="kind"
              value={value}
              checked={kind === value}
              onChange={() => setKind(value)}
            />
            {label}
          </label>
        ))}
      </fieldset>

      <Field
        label={kind === 'organisation' ? 'Organisation name' : 'Full name'}
        required
        value={name}
        onChange={(e) => setName(e.currentTarget.value)}
      />

      <fieldset className="flex flex-col gap-2">
        <legend className="text-sm font-medium text-ink">When do they get invoiced?</legend>
        {(
          [
            ['prepay', 'Prepay — invoiced at quote acceptance, paid before gear leaves'],
            ['account', 'On account — invoiced at check-in'],
          ] as const
        ).map(([value, label]) => (
          <label key={value} className="flex items-center gap-2 text-ink">
            <input
              type="radio"
              name="terms"
              value={value}
              checked={terms === value}
              onChange={() => setTerms(value)}
            />
            {label}
          </label>
        ))}
      </fieldset>

      {terms === 'account' ? (
        <Field
          label="Days until an invoice falls due"
          type="number"
          min={1}
          max={365}
          required
          value={accountDays}
          onChange={(e) => setAccountDays(e.currentTarget.value)}
        />
      ) : null}

      <AccountManagerPicker
        label="Account manager"
        current={null}
        value={manager === undefined ? (creatorEligible ? (me.data?.id ?? null) : null) : manager}
        onChange={setManager}
      />

      <Field
        label={contactRequired ? 'Contact name' : 'Contact name (optional)'}
        required={contactRequired}
        value={contactName}
        onChange={(e) => setContactName(e.currentTarget.value)}
      />
      <Field
        label="Email"
        type="email"
        hint="An email address or a phone number is needed — either will do."
        value={email}
        onChange={(e) => setEmail(e.currentTarget.value)}
      />
      <Field
        label="Phone"
        type="tel"
        value={phone}
        onChange={(e) => setPhone(e.currentTarget.value)}
      />

      <div className="flex gap-3">
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
        <Button type="button" variant="quiet" onClick={onCancel} disabled={save.isPending}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
